import { fabric } from "fabric";
import { type IImageOptions, Image } from "fabric/fabric-impl";
import { BackgroundImage, ExtendedObject } from "../types/objectTypes";
import { triggerObjectModifiedEvent } from "../helpers/canvasHelpers";
import { BackgroundImageFillMode, Canvas } from "../types/canvasTypes";

export function createBackgroundImage(options?: IImageOptions) {
  return new BackgroundImage("", {
    ...options,
    selectable: false,
    evented: false,
  });
}

export async function updateBackgroundWithImage({
  backgroundImage,
  image,
  canvas,
}: {
  backgroundImage: BackgroundImage;
  image: Image;
  canvas: Canvas;
}) {
  backgroundImage.setElement(image.getElement());

  return new Promise<BackgroundImage>((resolve) => {
    backgroundImage.setSrc(
      image.getSrc(),
      () => {
        triggerObjectModifiedEvent(canvas);
        postProcessBackgroundImageChanging(canvas);
        resolve(backgroundImage);
      },
      {
        crossOrigin: "anonymous",
      },
    );
  });
}

export async function createBackgroundImageFromImage({
  image,
  canvas,
  options,
}: {
  image: Image;
  canvas: Canvas;
  options?: IImageOptions;
}) {
  const backgroundImage = createBackgroundImage(options);
  backgroundImage.setElement(image.getElement());

  return new Promise<BackgroundImage>((resolve) => {
    backgroundImage.setSrc(
      image.getSrc(),
      () => {
        addBackgroundImageToCanvas(canvas, backgroundImage);
        postProcessBackgroundImageChanging(canvas);
        resolve(backgroundImage);
      },
      {
        crossOrigin: "anonymous",
      },
    );
  });
}

export function findBackgroundImage(canvas: Canvas): BackgroundImage | undefined {
  return canvas.getObjects().find((obj) => (obj as ExtendedObject).meaning === "background-image") as BackgroundImage;
}

export function createClipPathPointsFromViewPortDimensions(width: number, height: number) {
  const x = width + 1;
  const y = height + 1;

  return [
    { x: 0, y: 0 },
    { x, y: 0 },
    { x, y },
    { x: 0, y },
  ];
}

export function calculateBackgroundImageOptions({
  fill,
  viewPortWidth,
  viewPortHeight,
  backgroundImageWidth,
  backgroundImageHeight,
}: {
  fill: BackgroundImageFillMode;
  viewPortWidth: number;
  viewPortHeight: number;
  backgroundImageWidth: number;
  backgroundImageHeight: number;
}): Partial<BackgroundImage> {
  let scaleX = 1;
  let scaleY = 1;
  let top = 0;
  let left = 0;

  if (fill === "contain") {
    const imgAspectRatio = backgroundImageWidth / backgroundImageHeight;
    const containerAspectRatio = viewPortWidth / viewPortHeight;

    if (imgAspectRatio > containerAspectRatio) {
      scaleX = scaleY = viewPortWidth / backgroundImageWidth;
    } else {
      scaleX = scaleY = viewPortHeight / backgroundImageHeight;
    }
  } else if (fill === "cover") {
    scaleX = scaleY = Math.max(viewPortWidth / backgroundImageWidth, viewPortHeight / backgroundImageHeight);
  }

  left = (viewPortWidth - backgroundImageWidth * scaleX) / 2;
  top = (viewPortHeight - backgroundImageHeight * scaleY) / 2;

  const clipPath = new fabric.Polygon(createClipPathPointsFromViewPortDimensions(viewPortWidth, viewPortHeight), {
    absolutePositioned: true,
  });

  return {
    scaleX,
    scaleY,
    width: backgroundImageWidth,
    height: backgroundImageHeight,
    left,
    top,
    clipPath,
    backgroundImageFillMode: fill,
  };
}

export function initBackgroundImageOnCanvas(backgroundImage: BackgroundImage) {
  if (!backgroundImage.clipPath) return;

  backgroundImage.clipPath.absolutePositioned = true;
}

function addBackgroundImageToCanvas(canvas: Canvas, backgroundImage: BackgroundImage) {
  canvas.insertAt(backgroundImage, 1, false);
}

function postProcessBackgroundImageChanging(canvas: Canvas) {
  canvas.requestRenderAll();
}
